/**
 * Vertical hover-menu panel placement — keep the left-edge nub on-panel and
 * prefer a slight upward bias relative to the active trigger.
 */

export interface VerticalHoverMenuPanelGeometryInput {
	triggerTop: number;
	triggerHeight: number;
	panelHeight: number;
	viewportHeight: number;
	rootRight: number;
	bridgePx: number;
	/** Ideal distance from panel top to nub center. @default 28 */
	preferredNubFromTop?: number;
	/** Min inset so the rotated nub stays on the left edge. @default 14 */
	nubEdgePad?: number;
	/** Viewport margin. @default 8 */
	viewportPad?: number;
}

export interface VerticalHoverMenuPanelGeometry {
	top: number;
	left: number;
	nubOffset: number;
}

export interface CanShareVerticalHoverMenuPanelInput {
	panelTop: number;
	panelHeight: number;
	triggerTop: number;
	triggerHeight: number;
	/** Min inset so the rotated nub stays on the left edge. @default 14 */
	nubEdgePad?: number;
}

function clamp(n: number, lo: number, hi: number): number {
	if (hi < lo) {
		return lo;
	}
	return Math.min(hi, Math.max(lo, n));
}

/**
 * True when the trigger’s arrow would land on the existing panel’s left edge
 * (inside rounded-corner padding) — reuse that panel instead of opening a new one.
 */
export function canShareVerticalHoverMenuPanel(
	input: CanShareVerticalHoverMenuPanelInput,
): boolean {
	const nubEdgePad = input.nubEdgePad ?? 14;
	if (input.panelHeight <= nubEdgePad * 2) {
		return false;
	}
	const triggerCenterY = input.triggerTop + input.triggerHeight / 2;
	const nubOffset = triggerCenterY - input.panelTop;
	return (
		nubOffset >= nubEdgePad &&
		nubOffset <= input.panelHeight - nubEdgePad
	);
}

export function resolveVerticalHoverMenuPanelGeometry(
	input: VerticalHoverMenuPanelGeometryInput,
): VerticalHoverMenuPanelGeometry {
	const preferredNubFromTop = input.preferredNubFromTop ?? 28;
	const nubEdgePad = input.nubEdgePad ?? 14;
	const viewportPad = input.viewportPad ?? 8;
	const panelHeight = Math.max(0, input.panelHeight);
	const triggerCenterY = input.triggerTop + input.triggerHeight / 2;
	const left = input.rootRight + input.bridgePx;

	const minTop = viewportPad;
	const maxTop = Math.max(
		minTop,
		input.viewportHeight - panelHeight - viewportPad,
	);

	// Prefer the arrow near the top of the panel (panel sits slightly above the item).
	let top = triggerCenterY - preferredNubFromTop;
	top = clamp(top, minTop, maxTop);

	const minNub = Math.min(nubEdgePad, panelHeight / 2);
	const maxNub = Math.max(minNub, panelHeight - nubEdgePad);

	let nubOffset = triggerCenterY - top;
	if (nubOffset < minNub) {
		top = clamp(triggerCenterY - minNub, minTop, maxTop);
		nubOffset = triggerCenterY - top;
	} else if (nubOffset > maxNub) {
		top = clamp(triggerCenterY - maxNub, minTop, maxTop);
		nubOffset = triggerCenterY - top;
	}
	nubOffset = clamp(nubOffset, minNub, maxNub);

	return { top, left, nubOffset };
}

/** Nub Y for a trigger against a fixed shared panel top. */
export function resolveSharedVerticalHoverMenuNubOffset(input: {
	panelTop: number;
	panelHeight: number;
	triggerTop: number;
	triggerHeight: number;
	nubEdgePad?: number;
}): number {
	const nubEdgePad = input.nubEdgePad ?? 14;
	const triggerCenterY = input.triggerTop + input.triggerHeight / 2;
	const minNub = Math.min(nubEdgePad, input.panelHeight / 2);
	const maxNub = Math.max(minNub, input.panelHeight - nubEdgePad);
	return clamp(triggerCenterY - input.panelTop, minNub, maxNub);
}
